// Top bar: serif wordmark + current floor (admins: its actions menu; the building view: a menu with
// « Nouvel étage »), "CONNECTÉ À" connector chips (anchors of the dotted lines), engine badges,
// À VALIDER counter, « STUDIO » (the floor's ads, when it has a studio), calendar / automations
// buttons, live status, clock, user menu button (the suggestions are in its menu; admins: the
// suggestions waiting for their decision as a badge on it).
// Connector chips (SPEC-UX-V03 §E): one chip per service (its Claude and Codex entries merged),
// as many as the free width holds (measured with a ResizeObserver, window resize as a fallback);
// the others sit behind a « +N » button whose popover (hover, focus or click) lists them.
import { h, icon, setText, setAttr, classNames, clear, statusDot } from '../dom.js';
import { t } from '../i18n/fr.js';
import { formatClock, initials, hashHue } from '../format.js';
import { sortedFloors, pendingSuggestionCount } from '../store.js';
import { iconForConnector, serviceKey, bareConnectorName, normalizeConnectorName } from './connectoricons.js';
import { studioDepts } from './studio/model.js';

/** Chips shown when the free width cannot be measured (hidden bar, old browsers). */
const MAX_CHIPS = 12;
/** Geometry of the chips row (web/styles/topbar.css): chip edge, gap, room for « +N ». */
export const CHIP_LAYOUT = Object.freeze({ chip: 30, gap: 7, more: 40, pad: 4 });
/** The « +N » popover opened by hovering closes this long after the pointer left it. */
const HOVER_CLOSE_MS = 250;
const ENGINE_KEYS = ['claude', 'codex', 'openai-image', 'gemini', 'kie'];
/** Optional engines: their badge shows when they work, or when an agent relies on them (then red). */
const OPTIONAL_ENGINES = new Set(['openai-image', 'gemini', 'kie']);

/**
 * Which engine badges to show (pure): Claude and Codex always; an optional engine when it is
 * connected or when some agent of the office uses it (its failure then matters).
 * @param {object} status engines status
 * @param {object[]} agents
 * @returns {string[]} engine keys, in display order
 */
export function visibleEngines(status, agents = []) {
  const used = new Set((agents || []).map((a) => a?.engine).filter(Boolean));
  return ENGINE_KEYS.filter((key) => {
    const e = status?.[key];
    if (!e) return false;
    if (!OPTIONAL_ENGINES.has(key)) return true;
    return engineTone(e) === 'ok' || used.has(key);
  });
}

/**
 * The colour of an engine's dot (pure): « ok » (green) only for an engine checked connected
 * (installed, account signed in or key accepted); « off » (grey) while it is being checked or
 * when its connection could not be checked (network); « error » (red) otherwise — not connected,
 * not installed, out of credits. An old status without `state` falls back on `ok`.
 * @param {{ok?: boolean, state?: string, pending?: boolean}|null} e
 * @returns {'ok'|'off'|'error'}
 */
export function engineTone(e) {
  if (!e) return 'off';
  const state = e.state || (e.pending ? 'pending' : e.ok ? 'ok' : 'error');
  if (state === 'ok') return e.ok === false ? 'error' : 'ok';
  if (state === 'pending' || state === 'unknown' || e.pending) return 'off';
  return 'error';
}

/** The tooltip key of an engine's badge (pure): connected, not connected, not checked, checking, unavailable. */
export function engineTitleKey(e) {
  const tone = engineTone(e);
  if (tone === 'ok') return 'engine.ok';
  if (e?.state === 'pending' || e?.pending) return 'engine.pending';
  if (e?.state === 'unknown') return 'engine.unknown';
  if (e?.state === 'disconnected') return 'engine.disconnected';
  return 'engine.ko';
}
const CONNECTOR_TONE = { connected: 'ok', 'needs-auth': 'warn', failed: 'error', error: 'error', added: 'off', unknown: 'off' };
const STATUS_RANK = { connected: 0, 'needs-auth': 1, failed: 2, error: 2 };
const rankOf = (status) => STATUS_RANK[status] ?? 3;

/**
 * One entry per service (pure): the Claude and Codex entries of the same service
 * (« claude.ai Shopify » and « shopify (Codex) ») become one, with the best status, both keys and
 * both engines. Only across engines, and only for the same name once decorations are removed:
 * two servers of one brand (« claude.ai Shopify » and « shopify-fr », one store per floor) stay two
 * entries sharing the icon. Sorted: connected first, then needing authentication, then the
 * others; by name.
 * @param {Array<{key: string, name?: string, status?: string, engine?: string, engines?: string[]}>} connectors
 * @returns {Array<{id: string, key: string, keys: string[], name: string, label: string, status: string,
 *   engines: string[], members: object[]}>} key: the entry the chip stands for (a Claude one when there is one)
 */
export function mergeConnectors(connectors) {
  const groups = [];
  const ids = new Set();
  const engineOf = (c) => (c.engine === 'codex' ? 'codex' : 'claude');
  for (const c of connectors || []) {
    if (!c || typeof c.key !== 'string' || !c.key) continue;
    // A connector of the office's library is always its own chip (two Canva accounts are two).
    const same = c.library === true ? `lib:${c.key}` : normalizeConnectorName(c.name) || normalizeConnectorName(c.key) || c.key;
    // At most one entry per engine in a group: the Claude one and the Codex one of that service.
    const group = groups.find((g) => g.same === same && !g.members.some((m) => engineOf(m) === engineOf(c)));
    if (group) { group.members.push(c); continue; }
    const base = c.library === true ? c.key : serviceKey(c);
    const id = ids.has(base) ? `${base}~${c.key}` : base;
    ids.add(id);
    groups.push({ id, same, members: [c] });
  }
  const out = [];
  for (const { id, members } of groups) {
    const main = members.find((m) => m.engine !== 'codex') || members[0];
    const best = [...members].sort((a, b) => rankOf(a.status) - rankOf(b.status))[0];
    const engines = [...new Set(members.flatMap((m) => (Array.isArray(m.engines) && m.engines.length ? m.engines : [m.engine === 'codex' ? 'codex' : 'claude'])))];
    out.push({
      ...main,
      id,
      key: main.key,
      keys: members.map((m) => m.key),
      label: main.library === true ? String(main.label || main.name || main.key) : bareConnectorName(main.name || main.key),
      status: best.status || 'unknown',
      engines,
      members,
    });
  }
  return out.sort((a, b) => rankOf(a.status) - rankOf(b.status) || a.label.localeCompare(b.label, 'fr'));
}

/**
 * How many chips fit in `width` px (pure): all of them when they fit, else as many as leave room
 * for the « +N » button (0 when even one does not). An unknown width (0, NaN) gives `fallback`.
 * @param {number} count
 * @param {number} width the free width of the chips row
 * @param {{fallback?: number, layout?: typeof CHIP_LAYOUT}} [opts]
 */
export function fitChipCount(count, width, { fallback = MAX_CHIPS, layout = CHIP_LAYOUT } = {}) {
  const n = Math.max(0, Math.floor(Number(count) || 0));
  const w = Number(width);
  if (!Number.isFinite(w) || w <= 0) return Math.min(n, fallback);
  const room = w - layout.pad;
  const need = (k) => (k ? k * layout.chip + (k - 1) * layout.gap : 0);
  if (need(n) <= room) return n;
  let k = n - 1;
  while (k > 0 && need(k) + layout.gap + layout.more > room) k -= 1;
  return k;
}

/**
 * Creates the top bar.
 * @param {object} ctx app context ({store, t, …})
 * @param {{onApprovals: Function, onCalendar: Function, onAutomations: Function, onStudio?: (floorId: string) => void,
 *   onUserMenu: (anchor: HTMLElement) => void, onConnector?: (connector: object, chip: HTMLElement) => void,
 *   onLayout?: () => void, onFloorMenu?: (anchor: HTMLElement, floorId: string) => void,
 *   onBuildingMenu?: (anchor: HTMLElement) => void}} handlers
 *   onFloorMenu: admins get the floor's actions (rename, new department, new floor) from its name;
 *   onBuildingMenu: the same button in the building view (« Nouvel étage »);
 *   onConnector: a chip or an entry of the « +N » list; the connector carries `keys` (every entry of
 *   its service) and the element to anchor its details to
 * @returns {{el: HTMLElement, chipRects(): Map<string, DOMRect>, destroy(): void}}
 */
export function createTopbar(ctx, handlers) {
  const { store } = ctx;
  const offs = [];

  const floorName = h('span', { class: 'tb-floor' });
  const floorBtn = h('button', {
    type: 'button', class: 'tb-floor tb-floor-btn', hidden: true, 'aria-haspopup': 'menu',
    onClick: () => {
      const { ui } = store.get();
      if (ui.view === 'building') handlers.onBuildingMenu?.(floorBtn);
      else if (ui.floorId) handlers.onFloorMenu?.(floorBtn, ui.floorId);
    },
  }, h('span', { class: 'tb-floor-name' }), icon('chevronDown', { size: 14 }));
  const version = h('span', { class: 'wordmark-version' });
  const wordmark = h('a', { class: 'wordmark', href: '#/building', 'aria-label': t('topbar.home') },
    h('span', { class: 'wordmark-text' }, t('app.wordmark')), version);

  const chips = h('ul', { class: 'conn-chips', 'aria-label': t('topbar.connectors') });
  const connectLabel = h('span', { class: 'tb-connect-label mono-label' }, h('span', { class: 'live-dot', 'aria-hidden': 'true' }), t('topbar.connectedTo'));
  const connect = h('div', { class: 'tb-connect' }, connectLabel, chips);

  const engines = h('div', { class: 'tb-engines', role: 'group', 'aria-label': t('topbar.engines') });
  const approvalsCount = h('span', { class: 'approvals-count' }, '0');
  const approvals = h('button', { type: 'button', class: 'approvals-btn', onClick: () => handlers.onApprovals?.() },
    h('span', { class: 'approvals-label mono-label' }, t('topbar.approvals')), approvalsCount);
  const calendar = h('button', { type: 'button', class: 'tb-btn', title: `${t('menu.calendar')} (P)`, onClick: () => handlers.onCalendar?.() },
    icon('calendar', { size: 16 }), h('span', { class: 'tb-btn-label mono-label' }, t('topbar.calendar')));
  const automations = h('button', { type: 'button', class: 'tb-btn tb-btn-automations', title: t('menu.automations'), onClick: () => handlers.onAutomations?.() },
    icon('repeat', { size: 16 }), h('span', { class: 'tb-btn-label mono-label' }, t('topbar.automations')));
  // The floor's Studio (its ads), when the floor has one: a button of its own, not an engine.
  const studio = h('button', {
    type: 'button', class: 'tb-btn tb-btn-studio', hidden: true, title: t('topbar.studioTitle'),
    onClick: () => { const { ui } = store.get(); if (ui.floorId) handlers.onStudio?.(ui.floorId); },
  }, icon('film', { size: 16 }), h('span', { class: 'tb-btn-label mono-label' }, t('topbar.studio')));
  const live = h('span', { class: 'tb-live', role: 'img' });
  const clockHm = h('span', { class: 'clock-hm' });
  const clockS = h('span', { class: 'clock-s' });
  const clock = h('span', { class: 'tb-clock', role: 'timer', 'aria-label': t('topbar.clock') }, clockHm, clockS);
  // The suggestions live in the user menu (initials); admins see how many wait on the avatar.
  const avatarText = h('span', { class: 'avatar-initials' });
  const suggestionsCount = h('span', { class: 'avatar-badge', hidden: true, 'aria-hidden': 'true' });
  const avatar = h('button', { type: 'button', class: 'avatar-btn', 'aria-haspopup': 'menu', 'aria-expanded': 'false' }, avatarText, suggestionsCount);
  avatar.addEventListener('click', () => handlers.onUserMenu?.(avatar));

  const el = h('header', { class: 'topbar' },
    h('div', { class: 'tb-left' }, wordmark, h('span', { class: 'tb-sep', 'aria-hidden': 'true' }), floorName, floorBtn),
    connect,
    h('div', { class: 'tb-right' }, engines, approvals, studio, calendar, automations, h('span', { class: 'tb-time' }, live, clock), avatar));

  // --- connectors -----------------------------------------------------------------------------
  let chipIndex = new Map(); // connector key → chip element (connectors behind « +N » → that button)
  let services = [];         // mergeConnectors() of the store's list
  let shownCount = -1;       // chips drawn at the last render (-1: never)
  let moreBtn = null;
  let hidden = [];           // services behind « +N »
  let morePop = null;        // the open « +N » popover
  let moreMode = null;       // what opened it: 'hover' | 'focus' | 'click'
  let hoverTimer = null;
  let closedAt = 0;          // when it last closed (the focus coming back to « +N » must not reopen it)
  let destroyed = false;

  const statusText = (c) => t(`connector.status.${['connected', 'needs-auth', 'failed', 'added', 'error'].includes(c.status) ? c.status : 'unknown'}`);
  const engineNote = (c) => (c.engines.includes('claude') && c.engines.includes('codex') && c.keys.length > 1 ? ` · ${t('topbar.bothEngines')}` : '');

  function chipFor(c) {
    const status = statusText(c);
    const chip = h('button', {
      type: 'button', class: classNames(['conn-chip', `is-${CONNECTOR_TONE[c.status] || 'off'}`]),
      dataset: { key: c.key, service: c.id }, title: `${c.label} — ${status}${engineNote(c)}`,
      'aria-label': t('connector.chipAria', { name: c.label, status }),
      onClick: () => handlers.onConnector?.(c, chip),
    }, iconForConnector(c, { size: CHIP_LAYOUT.chip }), h('span', { class: 'conn-status', 'aria-hidden': 'true' }));
    return chip;
  }

  /** Closes the « +N » list; reason 'outside' leaves the focus where it went (not back on « +N »). */
  function closeMore(reason = 'api') {
    clearTimeout(hoverTimer);
    hoverTimer = null;
    const pop = morePop;
    morePop = null;
    moreMode = null;
    if (pop?.isOpen?.()) pop.close(reason);
  }

  /** An entry of the list: its details hang on « +N »; the focus they give back must not reopen the list. */
  function pickMore(c) {
    const anchor = moreBtn;
    closeMore();
    handlers.onConnector?.(c, anchor);
    const details = ctx.overlay?.top?.();
    if (details?.anchor === anchor) details.onClose?.(() => { closedAt = Date.now(); });
  }

  function moreList() {
    return h('ul', { class: 'conn-more-list', 'aria-label': t('topbar.moreList') }, hidden.map((c) => h('li', {},
      h('button', {
        type: 'button', class: 'conn-more-item', dataset: { key: c.key },
        onClick: () => pickMore(c),
      }, iconForConnector(c, { size: 20 }),
      h('span', { class: 'conn-more-name' }, c.label, engineNote(c) ? h('span', { class: 'conn-more-engines muted' }, engineNote(c)) : null),
      h('span', { class: 'conn-more-status' }, statusDot(CONNECTOR_TONE[c.status] || 'off'), h('span', {}, statusText(c)))))));
  }

  /** Opens the « +N » list (mode: what asked), or keeps it open and pins it on a click. */
  function openMore(mode) {
    if (!moreBtn || !hidden.length || !ctx.overlay?.popover) return;
    clearTimeout(hoverTimer);
    hoverTimer = null;
    if (morePop?.isOpen?.()) {
      if (mode === 'click' && moreMode !== 'click') {
        // Opened by hover or focus: the click (Enter on the focused button) pins it and enters it.
        moreMode = 'click';
        morePop.panel?.querySelector?.('.conn-more-item')?.focus?.();
      }
      return;
    }
    // The details of a connector of the list hang on « +N » too: hovering it must not close them
    // (a popover opened again from its anchor toggles off).
    if (mode !== 'click' && ctx.overlay.top?.()?.anchor === moreBtn) return;
    const active = globalThis.document?.activeElement;
    const pop = ctx.overlay.popover(moreBtn, moreList(), {
      placement: 'bottom-start', label: t('topbar.moreList'), className: 'conn-more-pop',
      // A click moves the focus into the list; hover and focus leave it where it is.
      initialFocus: mode === 'click' || !(typeof Element === 'function' && active instanceof Element) ? 'first' : active,
    });
    if (!pop?.isOpen?.()) return;
    morePop = pop;
    moreMode = mode;
    pop.onClose?.(() => {
      closedAt = Date.now();
      if (morePop === pop) { morePop = null; moreMode = null; }
    });
    pop.panel?.addEventListener?.('pointerenter', () => { clearTimeout(hoverTimer); hoverTimer = null; });
    pop.panel?.addEventListener?.('pointerleave', (e) => leaveMore(e));
  }

  function leaveMore(e) {
    if (moreMode !== 'hover' || e?.pointerType === 'touch') return;
    clearTimeout(hoverTimer);
    hoverTimer = setTimeout(() => { hoverTimer = null; if (moreMode === 'hover') closeMore(); }, HOVER_CLOSE_MS);
  }

  function renderChips() {
    const list = mergeConnectors(store.get().connectors);
    services = list;
    clear(chips);
    closeMore();
    moreBtn = null;
    hidden = [];
    chipIndex = new Map();
    connectLabel.classList.toggle('is-empty', !list.length); // no green dot next to « aucun connecteur »
    if (!list.length) {
      shownCount = 0;
      chips.appendChild(h('li', { class: 'conn-empty mono-label' }, t('topbar.noConnectors')));
      handlers.onLayout?.();
      return;
    }
    const n = fitChipCount(list.length, chips.clientWidth);
    shownCount = n;
    for (const c of list.slice(0, n)) {
      const chip = chipFor(c);
      chips.appendChild(h('li', {}, chip));
      for (const key of c.keys) chipIndex.set(key, chip);
    }
    if (list.length > n) {
      hidden = list.slice(n);
      const rest = hidden.length;
      const more = h('button', {
        type: 'button', class: 'conn-more mono-label', title: t('topbar.moreTitle', { n: rest }),
        'aria-label': t('topbar.moreAria', { n: rest }), 'aria-haspopup': 'dialog', 'aria-expanded': 'false',
        onClick: () => {
          if (morePop?.isOpen?.() && moreMode === 'click') closeMore();
          else openMore('click');
        },
      }, t('topbar.more', { n: rest }));
      more.addEventListener('pointerenter', (e) => { if (e?.pointerType !== 'touch') openMore('hover'); });
      more.addEventListener('pointerleave', (e) => leaveMore(e));
      more.addEventListener('focus', () => { if (!morePop?.isOpen?.() && Date.now() - closedAt > 300) openMore('focus'); });
      // Opened by the focus: tabbing away (not into the list) closes it, the focus left where it went.
      more.addEventListener('blur', (e) => {
        if (moreMode === 'focus' && !morePop?.panel?.contains?.(e?.relatedTarget)) closeMore('outside');
      });
      moreBtn = more;
      chips.appendChild(h('li', {}, more));
      for (const c of hidden) for (const key of c.keys) chipIndex.set(key, more);
    }
    handlers.onLayout?.();
  }

  /** The free width changed: redraw only when a different number of chips fits. */
  function refit() {
    if (destroyed || !services.length) return;
    if (fitChipCount(services.length, chips.clientWidth) !== shownCount) renderChips();
  }

  // --- engines --------------------------------------------------------------------------------
  function renderEngines() {
    const s = store.get();
    const st = s.engines || {};
    clear(engines);
    if (s.office?.mock || st.mock?.forced) {
      engines.appendChild(h('span', { class: 'engine-badge is-mock', title: t('app.mockTitle') }, t('app.mockBadge')));
    }
    for (const key of visibleEngines(st, s.office?.agents)) {
      const e = st[key];
      const name = t(`engine.name.${key}`);
      const tone = engineTone(e);
      const title = t(engineTitleKey(e), { name, version: e.version ? ` (${e.version})` : '', error: e.error ? ` — ${e.error}` : '' });
      engines.appendChild(h('span', { class: classNames(['engine-badge', `is-${tone}`]), title, role: 'img', 'aria-label': title },
        h('span', { class: 'engine-name mono-label', 'aria-hidden': 'true' }, t(`engine.${key}`)),
        h('span', { class: 'engine-dot', 'aria-hidden': 'true' })));
    }
  }

  // --- header texts ---------------------------------------------------------------------------
  function renderFloor() {
    const s = store.get();
    const floors = sortedFloors(s.office.floors);
    const f = floors.find((x) => x.id === s.ui.floorId);
    const onFloor = s.ui.view !== 'building' && !!f;
    const isAdmin = s.me?.role === 'admin';
    // Admins open the floor's menu from its name; in the building view (no current floor), the
    // same button offers « Nouvel étage ».
    // A floor whose studio is on offers its « Studio » entry to everyone who sees one of its services.
    const hasStudio = onFloor && studioDepts(s.office, f.id).length > 0;
    const menu = (isAdmin || hasStudio) && (onFloor ? !!handlers.onFloorMenu : !!handlers.onBuildingMenu);
    studio.hidden = !hasStudio || !handlers.onStudio;
    setAttr(studio, 'aria-label', hasStudio ? t('topbar.studioAria', { name: f.name }) : null);
    setText(floorName, onFloor ? f.name : t('elevator.building'));
    setText(floorBtn.firstChild, onFloor ? f.name : t('elevator.building'));
    setAttr(floorBtn, 'aria-label', onFloor ? t('topbar.floorMenu', { name: f.name }) : t('topbar.buildingMenu'));
    setAttr(floorBtn, 'title', onFloor ? t('topbar.floorMenuTitle') : t('topbar.buildingMenuTitle'));
    floorBtn.hidden = !menu;
    floorName.hidden = menu;
    setText(version, s.office.version ? `v${s.office.version.split('.').slice(0, 2).join('.')}` : '');
  }

  let lastCount = null;
  function renderCounts() {
    const n = Number(store.get().counts?.awaitingApproval) || 0;
    setText(approvalsCount, String(n));
    approvals.classList.toggle('has-items', n > 0);
    setAttr(approvals, 'aria-label', t('topbar.approvalsAria', { n }));
    if (lastCount !== null && n > lastCount) {
      approvals.classList.remove('is-bump');
      requestAnimationFrame(() => approvals.classList.add('is-bump'));
    }
    lastCount = n;
  }

  /** Admins: the suggestions waiting for their decision, as a badge on the avatar (hidden at 0; members never see a count). */
  function renderSuggestions() {
    const s = store.get();
    const n = s.me?.role === 'admin' ? pendingSuggestionCount(s.suggestions) : 0;
    setText(suggestionsCount, n > 99 ? '99+' : String(n));
    suggestionsCount.hidden = n === 0;
    avatar.classList.toggle('has-items', n > 0);
    const name = s.me?.displayName || s.me?.username || '';
    setAttr(avatar, 'aria-label', n > 0 ? `${t('topbar.userMenu', { name })} — ${t('topbar.suggestionsAria', { n })}` : t('topbar.userMenu', { name }));
  }

  function renderMe() {
    const me = store.get().me;
    const name = me?.displayName || me?.username || '';
    setText(avatarText, initials(name));
    setAttr(avatar, 'aria-label', t('topbar.userMenu', { name }));
    avatar.style.setProperty('--hue', String(hashHue(me?.username || name)));
    el.classList.toggle('is-admin', me?.role === 'admin');
    renderSuggestions();
  }

  function renderLive() {
    const s = store.get().sse || 'idle';
    live.className = classNames(['tb-live', `is-${s}`]);
    const label = t(`topbar.live.${s}`);
    setAttr(live, 'aria-label', label);
    setAttr(live, 'title', label);
  }

  function tick() {
    const s = formatClock(Date.now());
    setText(clockHm, s.slice(0, 5));
    setText(clockS, s.slice(5));
  }
  tick();
  const clockTimer = setInterval(tick, 1000);

  renderChips(); renderEngines(); renderFloor(); renderCounts(); renderMe(); renderLive(); renderSuggestions();
  // The chips follow the free width of their row; without ResizeObserver, the window's size.
  let observer = null;
  const win = globalThis.window;
  if (typeof globalThis.ResizeObserver === 'function') {
    observer = new globalThis.ResizeObserver(() => refit());
    observer.observe(chips);
  } else if (win?.addEventListener) {
    win.addEventListener('resize', refit);
    offs.push(() => win.removeEventListener('resize', refit));
  }
  offs.push(store.on('connectors', renderChips));
  offs.push(store.on('engines', renderEngines));
  offs.push(store.on('office', () => { renderEngines(); renderFloor(); }));
  offs.push(store.on('ui', renderFloor));
  offs.push(store.on('counts', renderCounts));
  offs.push(store.on('me', () => { renderMe(); renderFloor(); renderSuggestions(); }));
  offs.push(store.on('suggestions', renderSuggestions));
  offs.push(store.on('sse', renderLive));

  return {
    el,
    /** Bottom-centre anchor of each connector chip, in viewport coordinates. */
    chipRects() {
      const out = new Map();
      for (const [key, chip] of chipIndex) {
        const r = chip.getBoundingClientRect();
        if (r.width === 0 && r.height === 0) continue;
        out.set(key, r);
      }
      return out;
    },
    destroy() {
      destroyed = true;
      clearInterval(clockTimer);
      observer?.disconnect();
      observer = null;
      closeMore();
      for (const off of offs) off();
    },
  };
}
